import moment from 'moment';
import queryString from 'query-string';
import React, { useState, useEffect, useCallback } from 'react';
import _ from 'underscore';

import EmptyStateForReport from './empty_state_for_report.jsx';
import { PROGRESS_REPORTS_SELECTED_CLASSROOM_ID, } from './progress_report_constants';
import ProgressReportFilters from './progress_report_filters.jsx';

import { requestGet, } from '../../../../modules/request/index';
import { ReactTable, ReportHeader, accountGreyIcon, getPageTitle, safeParseHtml, Spinner } from '../../../Shared/index';
import { getTimeSpent, renderTooltipRow } from '../../helpers/studentReports';
import { useTableFilters } from '../../hooks/useTableFilters';

interface ActivitiesProgressReportProps {
  premiumStatus: string;
}

interface FilterOption {
  name: string;
  value: string | number;
  label: string;
}

// Premium Reports -> Data Export
const ActivitiesProgressReport: React.FC<ActivitiesProgressReportProps> = ({ premiumStatus }) => {

  useEffect(() => {
    document.title = getPageTitle("View Reports", "Data Export");
  }, []);

  const {
    currentFilters,
    setCurrentFilters,
    filterByField,
    getFilterOptions
  } = useTableFilters();

  const [loadingFilterOptions, setLoadingFilterOptions] = useState(true);
  const [loadingNewTableData, setLoadingNewTableData] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentSort, setCurrentSort] = useState({ sort_param: 'completed_at', sort_descending: true });
  const [results, setResults] = useState<any[]>([]);
  const [numPages, setNumPages] = useState(1);
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [classroomFilters, setClassroomFilters] = useState<FilterOption[]>([]);
  const [studentFilters, setStudentFilters] = useState<FilterOption[]>([]);
  const [unitFilters, setUnitFilters] = useState<FilterOption[]>([]);
  const [selectedClassroom, setSelectedClassroom] = useState<FilterOption | undefined>();
  const [selectedStudent, setSelectedStudent] = useState<FilterOption | undefined>();
  const [selectedUnit, setSelectedUnit] = useState<FilterOption | undefined>();

  const requestParams = useCallback(() => {
    let params: any = {
      page: currentPage + 1,
      ...currentFilters,
      ...currentSort
    };

    if (filtersLoaded) {
      params = { ...params, without_filters: true };
    }

    return params;
  }, [currentPage, currentFilters, currentSort, filtersLoaded]);

  const fetchData = useCallback(() => {
    setLoadingNewTableData(true);
    requestGet(
      queryString.stringifyUrl({
        url: `${window.postbuildEnv.DEFAULT_URL}/teachers/progress_reports/activity_sessions.json`,
        query: requestParams()
      }),
      (data) => {
        const newClassroomFilters = getFilterOptions(data.classrooms, 'name', 'id', 'All classes');
        const newStudentFilters = getFilterOptions(data.students, 'name', 'id', 'All students');
        const newUnitFilters = getFilterOptions(data.units, 'name', 'id', 'All activity packs');

        const selectedClassroomId = window.localStorage.getItem(PROGRESS_REPORTS_SELECTED_CLASSROOM_ID);

        if (!filtersLoaded && selectedClassroomId && newClassroomFilters.find(c => Number(c.value) === Number(selectedClassroomId))) {
          setClassroomFilters(newClassroomFilters);
          setStudentFilters(newStudentFilters);
          setUnitFilters(newUnitFilters);
          setFiltersLoaded(true);
          setCurrentFilters({ classroom_id: Number(selectedClassroomId) });
          // fetchData will be called again via useEffect watching currentFilters
        } else {
          setLoadingFilterOptions(false);
          setLoadingNewTableData(false);
          setResults(data.activity_sessions);
          setNumPages(data.page_count);
          setClassroomFilters(newClassroomFilters);
          setStudentFilters(newStudentFilters);
          setUnitFilters(newUnitFilters);

          if (!filtersLoaded) {
            setFiltersLoaded(true);
          }

          // Set selected filters after state updates
          setTimeout(() => {
            const selectedClassroom = newClassroomFilters.find(c => Number(c.value) === Number(selectedClassroomId)) || newClassroomFilters[0];
            setSelectedClassroom(selectedClassroom);
            setSelectedStudent(prevStudent => prevStudent || newStudentFilters[0]);
            setSelectedUnit(prevUnit => prevUnit || newUnitFilters[0]);
          }, 0);
        }
      }
    );
  }, [requestParams, filtersLoaded, getFilterOptions, setCurrentFilters]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (filtersLoaded && currentFilters.classroom_id) {
      fetchData();
    }
  }, [currentFilters]);

  const canViewReport = () => {
    return premiumStatus === 'paid' || premiumStatus === 'trial';
  };

  const nonPremiumBlur = () => {
    return canViewReport() ? '' : 'non-premium-blur';
  };

  const columnDefinitions = () => {
    const cellClassName = nonPremiumBlur();
    return [
      {
        Header: 'Student',
        accessor: 'student_id',
        resizeable: false,
        Cell: ({ row }: any) => {
          const { original } = row;
          const { id, link, student_id } = original;
          const label = studentFilters.find(student => student.value == student_id)?.name;
          return renderTooltipRow({ color: 'grey', icon: accountGreyIcon, id, label, link, headerWidth: 200 });
        },
        maxWidth: 200
      },
      {
        Header: 'Date',
        accessor: 'completed_at',
        resizeable: false,
        Cell: ({ row }: any) => moment.unix(Number(row.original.completed_at)).format('M/D/YY'),
        maxWidth: 90
      },
      {
        Header: 'Activity',
        accessor: 'activity_name',
        resizeable: false,
        Cell: ({ row }: any) => <span className="show-overflow">{safeParseHtml(row.original.activity_name)}</span>
      },
      {
        Header: 'Score',
        accessor: 'percentage',
        resizeable: false,
        Cell: ({ row }: any) => {
          const score = row.original.percentage >= 0 ? `${Math.round(row.original.percentage * 100)}%` : 'Completed';
          return <span className={cellClassName}>{score}</span>;
        },
        maxWidth: 90
      },
      {
        Header: 'Time spent',
        accessor: 'timespent',
        resizeable: false,
        Cell: ({ row }: any) => <span className={cellClassName}>{getTimeSpent(row.original.timespent)}</span>,
        maxWidth: 90
      },
      {
        Header: 'Standard',
        accessor: 'standard',
        resizeable: false,
        Cell: ({ row }: any) => row.original.standard ? row.original.standard.split(' ')[0] : '',
        maxWidth: 90
      },
      {
        Header: 'Tool',
        accessor: 'activity_classification_name',
        resizeable: false,
        maxWidth: 150
      }
    ];
  };

  const selectClassroom = (classroom: FilterOption) => {
    setSelectedClassroom(classroom);
    window.localStorage.setItem(PROGRESS_REPORTS_SELECTED_CLASSROOM_ID, String(classroom.value));
    filterByField('classroom_id', classroom.value, onFilterChange);
  };

  const selectStudent = (student: FilterOption) => {
    setSelectedStudent(student);
    filterByField('student_id', student.value, onFilterChange);
  };

  const selectUnit = (unit: FilterOption) => {
    setSelectedUnit(unit);
    filterByField('unit_id', unit.value, onFilterChange);
  };

  const onFilterChange = () => {
    setCurrentPage(0);
  };

  const reactTableSortedChange = (state: any) => {
    if (!state.length) {
      return;
    }

    const newSort = {
      sort_param: state[0].id,
      sort_descending: state[0].desc,
    };

    if (_.isEqual(currentSort, newSort)) {
      return;
    }

    setCurrentPage(0);
    setCurrentSort(newSort);
  };

  useEffect(() => {
    if (filtersLoaded) {
      fetchData();
    }
  }, [currentSort]);

  const reactTablePageChange = (newPage: number) => {
    if (currentPage === newPage) {
      return;
    }
    setCurrentPage(newPage);
  };

  useEffect(() => {
    if (filtersLoaded && currentPage > 0) {
      fetchData();
    }
  }, [currentPage]);

  const tableOrEmptyMessage = () => {
    let tableOrEmptyMessage;
    if (results.length) {
      tableOrEmptyMessage = (
        <ReactTable
          className='progress-report'
          columns={columnDefinitions()}
          currentPage={currentPage}
          data={results}
          defaultPageSize={Math.min(results.length, 25)}
          defaultSorted={[{ id: currentSort.sort_param, desc: currentSort.sort_descending }]}
          loading={loadingNewTableData}
          manualPageCount={numPages}
          manualPagination={true}
          manualSortBy={true}
          onPageChange={reactTablePageChange}
          onSortedChange={reactTableSortedChange}
          resizable={false}
          showPageSizeOptions={false}
          showPagination={true}
          showPaginationBottom={true}
          showPaginationTop={false}
        />
      );
    } else {
      tableOrEmptyMessage = <EmptyStateForReport />;
    }
    return (
      <div>
        <ProgressReportFilters
          classroomFilters={classroomFilters}
          filterTypes={['unit', 'classroom', 'student']}
          selectClassroom={selectClassroom}
          selectedClassroom={selectedClassroom}
          selectedStudent={selectedStudent}
          selectedUnit={selectedUnit}
          selectStudent={selectStudent}
          selectUnit={selectUnit}
          studentFilters={studentFilters}
          unitFilters={unitFilters}
        />
        {tableOrEmptyMessage}
      </div>
    );
  };

  const renderFiltersAndTable = () => {
    if (loadingFilterOptions || loadingNewTableData) {
      return <Spinner />;
    }
    return tableOrEmptyMessage();
  };

  const paramsToQueryString = () => {
    const obj = requestParams();
    return `?${Object.keys(obj).map(k => `${encodeURIComponent(k)}=${encodeURIComponent(obj[k])}`).join('&')}`;
  };

  const downloadReport = () => {
    if (canViewReport()) {
      return window.open(`/teachers/progress_reports/activity_sessions.csv${paramsToQueryString()}`);
    }
    alert('Downloadable reports are a Premium feature. You can visit Quill.org/premium to upgrade now!');
  };

  return (
    <main className='teacher-report-container progress-reports-2018 data-export'>
      <ReportHeader
        downloadReportButton={
          <button
            className='quill-button small green contained focus-on-light'
            onClick={downloadReport}
            style={{ display: 'block' }}
          >
            Download report
          </button>
        }
        headerText="Data Export"
        tooltipText='You can export the data as a CSV file by filtering for the classrooms, activity packs, or students you would like to export and then pressing "Download report."'
      />
      {renderFiltersAndTable()}
    </main>
  );
};

export default ActivitiesProgressReport;
